import { type BASE_API_REQ, DELETE, GET, POST, PUT, type ResponseType } from './base.api';
import { getDynamicInvalidationKeys, queryKeyRegistry } from '../utils/query.key.registry';
import {
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

type OnCompletedData<T> = { data: T; message: string; token: string };

type API_GENERATOR<InoutType extends Partial<BASE_API_REQ>, ReturnType> = {
  onCompleted?: (params: OnCompletedData<ReturnType>) => void;
  onError?: (err: unknown) => void;
} & Partial<InoutType & BASE_API_REQ>;

type ApiMethods = 'get' | 'put' | 'post' | 'delete';

type BypassCacheParams = { bypassCache?: boolean };

type ApiConfig = {
  skipAutoInvalidation?: boolean;
  /** Query route keys that are affected even when they differ from the mutation route. */
  invalidateQueryRoutes?: readonly string[];
};

const API_METHOD = {
  get: GET,
  put: PUT,
  post: POST,
  delete: DELETE,
};

// Type for the function returned for GET methods
type GetApiFunction<InputType extends Partial<BASE_API_REQ>, ReturnType> = (
  params: API_GENERATOR<InputType, ReturnType>,
  queryOptions?: BypassCacheParams & Partial<UseQueryOptions<ResponseType<ReturnType>, Error>>,
) => UseQueryResult<ResponseType<ReturnType>, Error>;

// Type for the function returned for mutation methods (put, post, delete)
type MutationApiFunction<InputType extends Partial<BASE_API_REQ>, ReturnType> = (
  params?: API_GENERATOR<InputType, ReturnType>,
) => UseMutationResult<ResponseType<ReturnType>, Error, API_GENERATOR<InputType, ReturnType>>;

// Overload signatures for generateAPI
function generateAPI<InputType extends Partial<BASE_API_REQ>, ReturnType>(
  apiMethod: 'get',
  route: string,
  config?: ApiConfig,
): GetApiFunction<InputType, ReturnType>;

function generateAPI<InputType extends Partial<BASE_API_REQ>, ReturnType>(
  apiMethod: 'put' | 'post' | 'delete',
  route: string,
  config?: ApiConfig,
): MutationApiFunction<InputType, ReturnType>;

// Implementation
function generateAPI<InputType extends Partial<BASE_API_REQ>, ReturnType>(
  apiMethod: ApiMethods,
  route: string,
  config: ApiConfig = {},
) {
  if (apiMethod === 'get') {
    return (
      params: API_GENERATOR<InputType, ReturnType>,
      queryOptions: BypassCacheParams &
        Partial<UseQueryOptions<ResponseType<ReturnType>, Error>> = {},
    ): UseQueryResult<ResponseType<ReturnType>, Error> => {
      const { onCompleted, onError, ...rest } = params;
      const { bypassCache, ...restQueryOptions } = queryOptions;

      const query = useQuery<ResponseType<ReturnType>, Error>({
        queryKey: [route, rest.query, rest.params, rest.headers],
        queryFn: async ({ signal }) => {
          const data = await API_METHOD[apiMethod]<ReturnType>(
            {
              ext: route,
              ...rest,
            },
            signal,
          );
          if (data.error) {
            throw new Error(data.error);
          }
          return data;
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        retry: false,
        staleTime: bypassCache ? 0 : 1000 * 60 * 5,
        ...restQueryOptions, // Spread after defaults so passed options can override
      });

      // Register the query key in the registry for dynamic invalidation (unless skipped)
      useEffect(() => {
        if (!config.skipAutoInvalidation) {
          queryKeyRegistry.addQueryKey(route);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [route, config.skipAutoInvalidation]);

      useEffect(() => {
        if (query.isSuccess && query.data) {
          if (query.data.statusCode === 200 || query.data.statusCode === 201) {
            onCompleted?.({
              data: query.data.payload,
              token: query.data.token ?? '',
              message: query.data.message,
            });
          }
        }
      }, [query.isSuccess, query.data, onCompleted]);

      useEffect(() => {
        if (query.isError && query.error) {
          onError?.(query.error);
        }
      }, [query.isError, query.error, onError]);

      return query;
    };
  }

  return (
    params: API_GENERATOR<InputType, ReturnType> = {},
  ): UseMutationResult<ResponseType<ReturnType>, Error, API_GENERATOR<InputType, ReturnType>> => {
    const { onCompleted, onError } = params;
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (dto) => {
        const data = await API_METHOD[apiMethod]<ReturnType>({
          ext: route,
          ...dto,
        });
        if (data.error || data.statusCode < 200 || data.statusCode >= 300) {
          throw new Error(data.error || data.message || `Request failed with status ${data.statusCode}`);
        }
        return data;
      },
      onSuccess: async (data) => {
        // Dynamic query invalidation based on registered query keys (unless skipped)
        if (!config.skipAutoInvalidation) {
          const invalidationKeys = getDynamicInvalidationKeys(route);
          const affectedRoutes = [
            ...invalidationKeys,
            ...(config.invalidateQueryRoutes ?? []),
          ];
          await Promise.all(
            [...new Set(affectedRoutes)].map((key) =>
              queryClient.invalidateQueries({ queryKey: [key] }),
            ),
          );
        }
        // Complete active-query refetches before drawers close or success UI runs.
        if (data.statusCode === 200 || data.statusCode === 201) {
          onCompleted?.({
            data: data.payload,
            token: data.token ?? '',
            message: data.message,
          });
        }
      },
      onError: (err) => {
        onError?.(err);
      },
    });
  };
}

export default generateAPI;
